import React, { createContext, useContext, ReactNode } from "react";
import axios, { AxiosInstance } from "axios";
import axiosMiddleware from "../components/util/AxiosMiddleware";
import { useMsalDynamic } from "./MsalDynamicProvider";
import { useTranslation } from "react-i18next";
import { useStore } from "../index";
import { IClausionStateStore } from "../../zustand/store";
import { useShallow } from "zustand/shallow";

interface AxiosContextType {
  axiosInstance: AxiosInstance;
}

const AxiosContext = createContext<AxiosContextType | undefined>(undefined);

interface AxiosProviderProps {
  children: ReactNode;
}

export const AxiosProvider: React.FC<AxiosProviderProps> = ({ children }) => {
  const { msalInstance } = useMsalDynamic();
  const { i18n } = useTranslation();
  const serviceUrl = useStore(useShallow((state: IClausionStateStore) => state.serviceUrl));
  const addRecentServiceUrl = useStore(useShallow((state: IClausionStateStore) => state.addRecentServiceUrl));
  const addDrErrorValue = useStore(useShallow((state: IClausionStateStore) => state.setDrErrorValue));

  // Create axios instance with once
  const axiosInstance = React.useMemo(() => {
    const instance = axios.create();
    return instance;
  }, []);

  // Store interceptor ID so we can eject it
  const interceptorId = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (msalInstance) {
      axiosInstance.defaults.baseURL = "https://" + serviceUrl;
      // Add interceptor and store its ID
      interceptorId.current = axiosInstance.interceptors.request.use((config) => axiosMiddleware(config, msalInstance));
      axiosInstance.get("/api/auth/user").then((response) => {
        i18n.changeLanguage(response.data.uiLanguage.toLowerCase());
        addRecentServiceUrl(serviceUrl);
      });
      axiosInstance.get("/api/DR/getcustomparameter", {
          params: {
            parameterName: "drerrorvalue",
          },
        })
        .then((response) => {
          addDrErrorValue(response.data.responseMessage.message);
        });
    } else if (interceptorId.current !== null) {
      // Eject interceptor if msalInstance is not available
      axiosInstance.interceptors.request.eject(interceptorId.current);
      interceptorId.current = null;
    }
    // If msalInstance is not yet initialized, requests will not have auth
  }, [msalInstance]);

  const contextValue = React.useMemo(
    () => ({
      axiosInstance,
    }),
    [axiosInstance]
  );

  return <AxiosContext.Provider value={contextValue}>{children}</AxiosContext.Provider>;
};

export const useAxios = (): AxiosContextType => {
  const context = useContext(AxiosContext);
  if (context === undefined) {
    throw new Error("useAxios must be used within an AxiosProvider");
  }
  return context;
};
